/**
 * Priority assessment: a short questionnaire scored as Impact x Urgency (the
 * standard service-desk matrix), so priority reflects the actual business
 * effect instead of whatever the requester picks. Stored on the ticket as
 * `priority_assessment` (00571) so anyone can see why it got its priority.
 *
 *   Impact  = how bad and how widespread (production effect, people affected,
 *             security/data concern).
 *   Urgency = how soon it bites (deadline, whether work can continue).
 *
 *                 Urgency High   Urgency Medium   Urgency Low
 *   Impact High   Critical       High             High
 *   Impact Medium High           Medium           Medium
 *   Impact Low    Medium         Low              Low
 *
 * Pure functions only, so the form, the ticket page, and the XPM Agent all
 * score a ticket identically.
 */
import type { TicketPriority } from '../types/ticket'

export type Level = 'High' | 'Medium' | 'Low'

interface Option<V extends string> { value: V; label: string; hint?: string }
interface Question<K extends string, V extends string> { key: K; label: string; options: Option<V>[] }

export const PRODUCTION_OPTIONS = [
  { value: 'down', label: 'Production is down', hint: 'A live system or business process has stopped' },
  { value: 'degraded', label: 'Production is degraded', hint: 'Working, but slow, partly broken, or giving wrong results' },
  { value: 'minor', label: 'Minor production effect', hint: 'An annoyance; the business is not materially affected' },
  { value: 'none', label: 'No production impact', hint: 'Non-production, a question, training, or an enhancement' },
] as const satisfies readonly Option<string>[]

export const USERS_OPTIONS = [
  { value: 'organization', label: 'The whole organization or a site' },
  { value: 'department', label: 'A department or team' },
  { value: 'few', label: 'A few people' },
  { value: 'one', label: 'Just one person' },
] as const satisfies readonly Option<string>[]

export const WORKAROUND_OPTIONS = [
  { value: 'none', label: 'No, work is blocked' },
  { value: 'painful', label: 'Yes, but it is slow or manual' },
  { value: 'easy', label: 'Yes, an easy one' },
] as const satisfies readonly Option<string>[]

export const DEADLINE_OPTIONS = [
  { value: 'today', label: 'Today', hint: 'e.g. payroll, month-end close, a client deliverable' },
  { value: 'week', label: 'This week' },
  { value: 'later', label: 'Later, or no deadline' },
] as const satisfies readonly Option<string>[]

export const SECURITY_OPTIONS = [
  { value: 'confirmed', label: 'Yes', hint: 'Data is exposed, wrong, lost, or access is compromised' },
  { value: 'possible', label: 'Possibly' },
  { value: 'no', label: 'No' },
] as const satisfies readonly Option<string>[]

export type ProductionAnswer = (typeof PRODUCTION_OPTIONS)[number]['value']
export type UsersAnswer = (typeof USERS_OPTIONS)[number]['value']
export type WorkaroundAnswer = (typeof WORKAROUND_OPTIONS)[number]['value']
export type DeadlineAnswer = (typeof DEADLINE_OPTIONS)[number]['value']
export type SecurityAnswer = (typeof SECURITY_OPTIONS)[number]['value']

export interface AssessmentAnswers {
  production: ProductionAnswer
  users: UsersAnswer
  workaround: WorkaroundAnswer
  deadline: DeadlineAnswer
  security: SecurityAnswer
}

type AnyQuestion = Question<keyof AssessmentAnswers, string>

/** In display order. */
export const ASSESSMENT_QUESTIONS: AnyQuestion[] = [
  { key: 'production', label: 'What is the impact to production?', options: [...PRODUCTION_OPTIONS] },
  { key: 'users', label: 'Who is affected?', options: [...USERS_OPTIONS] },
  { key: 'workaround', label: 'Can work continue another way?', options: [...WORKAROUND_OPTIONS] },
  { key: 'deadline', label: 'When does this need to be fixed to avoid business harm?', options: [...DEADLINE_OPTIONS] },
  { key: 'security', label: 'Is there a security or data integrity concern?', options: [...SECURITY_OPTIONS] },
]

export interface AssessmentResult {
  impact: Level
  urgency: Level
  priority: TicketPriority
  /** One plain-English line per factor that drove the result, for the form and ticket page. */
  reasons: string[]
}

const MATRIX: Record<Level, Record<Level, TicketPriority>> = {
  High: { High: 'Critical', Medium: 'High', Low: 'High' },
  Medium: { High: 'High', Medium: 'Medium', Low: 'Medium' },
  Low: { High: 'Medium', Medium: 'Low', Low: 'Low' },
}

const RANK: Record<Level, number> = { Low: 0, Medium: 1, High: 2 }
const maxLevel = (...levels: Level[]): Level => levels.reduce((a, b) => (RANK[b] > RANK[a] ? b : a), 'Low')

const label = (opts: readonly Option<string>[], v: string) => opts.find((o) => o.value === v)?.label ?? v

export function assessPriority(a: AssessmentAnswers): AssessmentResult {
  const wide = a.users === 'organization' || a.users === 'department'

  // Impact: the worst of the production effect (scaled by reach) and any
  // security/data concern.
  const productionImpact: Level =
    a.production === 'down' ? 'High'
    : a.production === 'degraded' ? (wide ? 'High' : 'Medium')
    : a.production === 'minor' ? (wide ? 'Medium' : 'Low')
    : 'Low'
  const securityImpact: Level = a.security === 'confirmed' ? 'High' : a.security === 'possible' ? 'Medium' : 'Low'
  const impact = maxLevel(productionImpact, securityImpact)

  // Urgency: the nearest deadline, or blocked work on something live. A
  // confirmed security/data problem is always urgent: exposure keeps growing.
  const blockedLive = a.workaround === 'none' && a.production !== 'none'
  const deadlineUrgency: Level = a.deadline === 'today' ? 'High' : a.deadline === 'week' ? 'Medium' : 'Low'
  const workUrgency: Level = blockedLive ? 'High' : a.workaround === 'painful' && a.production !== 'none' ? 'Medium' : 'Low'
  const urgency = maxLevel(deadlineUrgency, workUrgency, a.security === 'confirmed' ? 'High' : 'Low')

  const reasons: string[] = []
  if (a.production !== 'none') {
    reasons.push(`${label(PRODUCTION_OPTIONS, a.production)}, affecting ${label(USERS_OPTIONS, a.users).toLowerCase()}`)
  }
  if (a.security !== 'no') reasons.push(a.security === 'confirmed' ? 'Confirmed security or data concern' : 'Possible security or data concern')
  if (blockedLive) reasons.push('No workaround, work is blocked')
  else if (a.workaround === 'painful' && a.production !== 'none') reasons.push('Only a slow or manual workaround')
  if (a.deadline !== 'later') reasons.push(`Needed ${a.deadline === 'today' ? 'today' : 'this week'}`)
  if (reasons.length === 0) reasons.push('No production impact and no deadline')

  return { impact, urgency, priority: MATRIX[impact][urgency], reasons }
}

/** What's saved on the ticket (it_tickets.priority_assessment, 00571). */
export interface PriorityAssessment {
  version: 1
  answers: AssessmentAnswers
  impact: Level
  urgency: Level
  /** The priority the questionnaire produced. The ticket's priority may differ if overridden. */
  suggested: TicketPriority
  /** Set when an agent/admin chose a different priority than `suggested`. */
  overrideReason?: string
  assessedAt: string
}

export function buildAssessment(answers: AssessmentAnswers, overrideReason?: string): PriorityAssessment {
  const r = assessPriority(answers)
  return {
    version: 1,
    answers,
    impact: r.impact,
    urgency: r.urgency,
    suggested: r.priority,
    ...(overrideReason?.trim() ? { overrideReason: overrideReason.trim() } : {}),
    assessedAt: new Date().toISOString(),
  }
}

/** Defensive read of the stored JSON (null for tickets filed before 00571 or malformed data). */
export function parseAssessment(raw: unknown): PriorityAssessment | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Partial<PriorityAssessment>
  const a = r.answers as Partial<AssessmentAnswers> | undefined
  if (r.version !== 1 || !a) return null
  const ok =
    PRODUCTION_OPTIONS.some((o) => o.value === a.production)
    && USERS_OPTIONS.some((o) => o.value === a.users)
    && WORKAROUND_OPTIONS.some((o) => o.value === a.workaround)
    && DEADLINE_OPTIONS.some((o) => o.value === a.deadline)
    && SECURITY_OPTIONS.some((o) => o.value === a.security)
  if (!ok) return null
  const answers = a as AssessmentAnswers
  const result = assessPriority(answers)
  return {
    version: 1,
    answers,
    impact: result.impact,
    urgency: result.urgency,
    suggested: result.priority,
    overrideReason: typeof r.overrideReason === 'string' && r.overrideReason ? r.overrideReason : undefined,
    assessedAt: typeof r.assessedAt === 'string' ? r.assessedAt : '',
  }
}

/** Label for an answer value, for read-only display on the ticket page. */
export function answerLabel(key: keyof AssessmentAnswers, value: string): string {
  const q = ASSESSMENT_QUESTIONS.find((x) => x.key === key)
  return q ? label(q.options, value) : value
}

/** True once every question has an answer. */
export function isComplete(a: Partial<AssessmentAnswers>): a is AssessmentAnswers {
  return ASSESSMENT_QUESTIONS.every((q) => !!a[q.key])
}
